import React from 'react';

/**
 * SoftwareTool - PC Software Deployment Tool
 * Allows users to deploy software packages to workstations
 */
export const SoftwareTool: React.FC = () => {
  return (
    <div className="flex-1 overflow-auto bg-gradient-to-br from-[#F8F9FA] via-white to-[#E6F0F9]">
      <div className="max-w-5xl mx-auto p-8 relative">
        <img 
          src="/images/ottie.svg" 
          alt="Otti mascot" 
          aria-hidden="true" 
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 w-40 md:w-48" 
          style={{ top: '-48px', zIndex: -1 }} 
        />
        
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-[#0054A4] mb-2">PC Software Deployment</h2>
          <p className="text-gray-600">Install and manage software on workstations and endpoints</p>
        </div>

        {/* Main Software Installation Panel */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#0054A4]/10 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-[#0054A4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-[#1E3A5F]">Software Installation</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Business Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Unit</label>
              <select className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0054A4]">
                <option>Select Business Unit</option>
                <option>RBU (Regional Business Unit)</option>
                <option>Australian Business Unit</option>
                <option>North America Operations</option>
                <option>Europe Operations</option>
                <option>Asia Pacific</option>
                <option>Global IT Services</option>
                <option>EAZ (Enterprise Admin Zone)</option>
              </select>
            </div>

            {/* Target PC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target PC</label>
              <select className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0054A4]">
                <option>Select PC</option>
                <option>PC-FIN-001 (Finance - John Smith)</option>
                <option>PC-HR-018 (HR - Sarah Johnson)</option>
                <option>PC-IT-091 (IT Ops - Mike Davis)</option>
                <option>PC-OPS-042 (Operations - Lisa Chen)</option>
                <option>PC-ENG-125 (Engineering - Tom Wilson)</option>
                <option>PC-MFG-203 (Manufacturing - Ana Garcia)</option>
                <option>PC-SEC-077 (Security - David Brown)</option>
                <option>PC-OT-156 (OT Admin - Maria Rodriguez)</option>
              </select>
            </div>

            {/* Software Package */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Software Package</label>
              <select className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0054A4]">
                <option>Select Software</option>
                <optgroup label="OT & Industrial Security">
                  <option>Claroty Platform (OT Security)</option>
                  <option>Nozomi Networks Guardian</option>
                  <option>Dragos Platform</option>
                  <option>Tenable OT Security</option>
                  <option>SteelCloud ConfigOS</option>
                </optgroup>
                <optgroup label="Backup & Recovery">
                  <option>Veeam Immutable Backup</option>
                  <option>Veeam Backup & Replication</option>
                </optgroup>
                <optgroup label="Development Tools">
                  <option>Visual Studio Code</option>
                  <option>Git for Windows</option>
                  <option>Python 3.x</option>
                  <option>Docker Desktop</option>
                </optgroup>
                <optgroup label="Utilities">
                  <option>Chrome Browser</option>
                  <option>Firefox Browser</option>
                  <option>7-Zip</option>
                  <option>VLC Media Player</option>
                  <option>Notepad++</option>
                </optgroup>
              </select>
            </div>

            {/* Version */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
              <select className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0054A4]">
                <option>Latest (Recommended)</option>
                <option>2024.2</option>
                <option>2024.1</option>
                <option>2023.12</option>
                <option>2023.11</option>
                <option>2023.10 LTS</option>
                <option>Custom Version...</option>
              </select>
            </div>
          </div>

          {/* Schedule Section */}
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-3">Deployment Schedule (Optional)</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input type="date" className="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#0054A4]" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Time</label>
                <input type="time" className="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#0054A4]" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Maintenance Window</label>
                <select className="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#0054A4]">
                  <option>Immediate</option>
                  <option>Next Available</option>
                  <option>Weekend (Sat 2AM)</option>
                  <option>After Hours (8PM-6AM)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Deploy Button */}
          <button className="w-full bg-[#0054A4] hover:bg-[#003A73] text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Deploy Software
          </button>

          {/* Recent Deployments */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-700">Recent PC Deployments</p>
              <button className="text-xs text-[#0054A4] hover:text-[#003A73]">View All →</button>
            </div>
            <div className="space-y-2">
              <div className="text-sm bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-900 font-medium">Claroty Platform</span>
                  <span className="text-green-600 text-xs font-medium">✓ Completed</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-xs text-gray-500">PC-OPS-042 • RBU Operations</span>
                  <span className="text-xs text-gray-400">10 min ago</span>
                </div>
              </div>
              <div className="text-sm bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-900 font-medium">Axonius Asset Management</span>
                  <span className="text-yellow-600 text-xs font-medium">⟳ Installing...</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-xs text-gray-500">PC-IT-015 • Global IT Services</span>
                  <span className="text-xs text-gray-400">In progress</span>
                </div>
              </div>
              <div className="text-sm bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-900 font-medium">CrowdStrike Falcon Sensor</span>
                  <span className="text-green-600 text-xs font-medium">✓ Completed</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-xs text-gray-500">PC-SEC-077 • Australian Business Unit</span>
                  <span className="text-xs text-gray-400">1h ago</span>
                </div>
              </div>
              <div className="text-sm bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-900 font-medium">Microsoft Office 365</span>
                  <span className="text-green-600 text-xs font-medium">✓ Completed</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-xs text-gray-500">PC-FIN-001 • North America Operations</span>
                  <span className="text-xs text-gray-400">2h ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoftwareTool;
